import { z } from "zod";
import { EBillRateType, schemes } from "@zeal/common";
import {
  ApprovalAndDeliverySchema,
  DiscountsAndFeesSchema,
  NotesSchema,
  PaymentSchema,
} from "../createInvoiceTemplate/createInvoiceTemplate.schema";

export enum EInvoiceStatus {
  PAID = "paid",
  PENDING = "pending",
  OVERDUE = "overdue",
  CANCELLED = "cancelled",
  DRAFT = "draft",
  SENT = "sent",
  VIEWED = "viewed",
  UNCOLLECTIBLE = "uncollectible",
  APPROVED = "approved",
  Failed = "failed",
}

export const ShiftEarningsSchema = z.object({
  hours: z.number(),
  wage: z.number(),
  bill_rate: z.number().optional(),
  bill_rate_type: z.nativeEnum(EBillRateType).optional(),
  amount: z.number().optional(),
});

export const ShiftSchema = z.object({
  shiftID: z.string(),
  employeeID: z.string().optional(),
  time: z.string(),
  first_name: z.string(),
  middle_initial: z.string().optional(),
  last_name: z.string(),
  hourly: ShiftEarningsSchema.optional(),
  overtime: ShiftEarningsSchema.optional(),
  doubletime: ShiftEarningsSchema.optional(),
  pto: ShiftEarningsSchema.optional(),
  vacation: ShiftEarningsSchema.optional(),
  sick: ShiftEarningsSchema.optional(),
  holiday: ShiftEarningsSchema.optional(),
  piecework: ShiftEarningsSchema.optional(),
  flat: ShiftEarningsSchema.pick({
    hours: true,
    amount: true,
  }).optional(),
  correction: ShiftEarningsSchema.pick({
    hours: true,
    amount: true,
    bill_rate: true,
  }).optional(),
  meal_break: z
    .object({
      units: z.number(),
      amount_per_unit: z.number(),
    })
    .optional(),
  rest_break: z
    .object({
      units: z.number(),
      amount_per_unit: z.number(),
    })
    .optional(),
  workLocationID: z.string().nullable().optional(),
  workLocationName: z.string().nullable().optional(),
  customerAccountID: z.string(),
  jobID: z.string().nullable().optional(),
  jobName: z.string().nullable().optional(),
  departmentID: z.string().nullable().optional(),
  departmentName: z.string().nullable().optional(),
});

export const invoiceContractorPaymentSchema = z.object({
  _id: z.string(),
  pay_date: z.string(),
  first_name: z.string(),
  last_name: z.string(),
  amount: z.number(),
  workLocationID: z.string().nullable().optional(),
  customerAccountID: z.string(),
  jobID: z.string().nullable().optional(),
  jobName: z.string().nullable().optional(),
  departmentID: z.string().nullable().optional(),
  departmentName: z.string().nullable().optional(),
});

export const createInvoiceRequestSchema = z.object({
  invoiceTemplateID: z.string(),
  customerAccountID: z.string(),
  status: z.nativeEnum(EInvoiceStatus).optional(),
  invoice_number: z.string(),
  items: z.array(
    z.object({
      id: z.string(),
      description: z.string(),
      quantity: z.number().optional(),
      price: z.number().optional(),
      is_removable: z.boolean().optional(),
    })
  ),
  employee_items: z.array(ShiftSchema),
  contractor_items: z.array(invoiceContractorPaymentSchema),
  discounts_and_fees: DiscountsAndFeesSchema,
  payment: PaymentSchema,
  notes: NotesSchema,
  approval_and_delivery: ApprovalAndDeliverySchema,
  invoice_date: z.string(),
  due_date: z.string(),
  amount: z.number(),
  /** Sum of employee + contractor + items (cents). Optional; computed on backend if omitted. */
  subtotal: z.number().optional(),
  /** subtotal minus discounts (cents). Used for percentage fees. Optional. */
  subtotal_minus_discounts: z.number().optional(),
});

export type TInvoiceContractorPayment = z.infer<
  typeof invoiceContractorPaymentSchema
>;

const UserInfoSchema = z.object({
  user_id: z.string().optional(),
  email: z.string().optional(),
  name: z.string().optional(),
});

const EmailHistoryItemSchema = z.object({
  sent_at: z.string(),
  sent_by: UserInfoSchema.optional(),
  sent_to: z.array(z.string()),
  email_type: z.enum([
    "initial",
    "reminder",
    "overdue_reminder",
    "paid",
    "failed",
  ]),
  is_automatic: z.boolean(),
});

const EmailViewSchema = z.object({
  viewed_at: z.string(),
  viewed_by: z.string().optional(),
  ip_address: z.string().optional(),
  user_agent: z.string().optional(),
  view_type: z.enum(["email_open", "page_visit"]).optional(),
});

export const createInvoiceResponseSchema = createInvoiceRequestSchema.extend({
  invoiceID: schemes.uuid(),
  companyID: schemes.companyID(),
  status: z.nativeEnum(EInvoiceStatus),
  subtotal: z.number().optional(),
  subtotal_minus_discounts: z.number().optional(),
  payment_status: z.string().optional(),
  first_sent_date: z.string().optional(),
  last_reminder_sent_date: z.string().optional(),
  paid_notification_sent: z.boolean().optional(),
  failed_notification_sent: z.boolean().optional(),
  due_notification_sent: z.boolean().optional(),
  previous_status: z.nativeEnum(EInvoiceStatus).optional(),
  created_by: UserInfoSchema.optional(),
  updated_by: UserInfoSchema.optional(),
  email_history: z.array(EmailHistoryItemSchema).optional(),
  email_views: z.array(EmailViewSchema).optional(),
  payout_transaction_id: z.string().optional(),
  createdAt: z.string().optional(),
  updatedAt: z.string().optional(),
});

export const createInvoicesRequestSchema = z.object({
  invoices: z.array(createInvoiceRequestSchema),
});
export const createInvoicesResponseSchema = z.object({
  invoices: z.array(createInvoiceResponseSchema),
});

export type TCreateInvoiceRequest = z.infer<typeof createInvoiceRequestSchema>;
export type TCreateInvoicesRequest = z.infer<
  typeof createInvoicesRequestSchema
>;
export type TCreateInvoiceResponse = z.infer<
  typeof createInvoiceResponseSchema
>;
export type TCreateInvoicesResponse = z.infer<
  typeof createInvoicesResponseSchema
>;
